import React from 'react';

/*
 * Single metadata component. Uses React 19 native document metadata hoisting —
 * <title>, <meta> and <link> render anywhere in the tree and React moves them to
 * <head>. No react-helmet, so nothing breaks on the next React upgrade.
 *
 * JSON-LD is rendered inline: Google reads it anywhere in the DOM, and it unmounts
 * with the page instead of leaking into the next route like Helmet-injected schema.
 */
const Meta = ({
  title,
  description,
  canonical,
  image = 'https://safeedges.in/og-image.png',
  imageAlt = 'Safe Edges — Blockchain Security Audit Firm',
  type = 'website',
  noindex = false,
  schemas = [],
  /*
   * hreflang alternates. Defaults to a self-referencing en / x-default pair, which is
   * valid on a single-language site. Pages that belong to a real regional cluster (the
   * /locations tree) pass the full cluster instead — see src/data/locations.js.
   */
  alternates,
}) => (
  <>
    <title>{title}</title>
    <meta name="description" content={description} />
    <link rel="canonical" href={canonical} />
    {(alternates || [
      { hreflang: 'en', href: canonical },
      { hreflang: 'x-default', href: canonical },
    ]).map((a) => (
      <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
    ))}
    <meta name="author" content="Safe Edges" />
    <meta
      name="robots"
      content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
    />

    <meta property="og:type" content={type} />
    <meta property="og:url" content={canonical} />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:image" content={image} />
    <meta property="og:image:alt" content={imageAlt} />
    <meta property="og:site_name" content="Safe Edges" />
    <meta property="og:locale" content="en_US" />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:url" content={canonical} />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={image} />
    <meta name="twitter:image:alt" content={imageAlt} />
    <meta name="twitter:site" content="@SafeEdges" />
    <meta name="twitter:creator" content="@SafeEdges" />

    {schemas.filter(Boolean).map((schema, i) => (
      <script
        key={i}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    ))}
  </>
);

export default Meta;
